import { embedIccProfile, readHeifIccProfile, toSrgbJpeg } from './iccProfile';

/**
 * Major brands in the `ftyp` box that mark a HEIF container, the format iPhone photos are saved in.
 * Matches the list `heic-to` checks, so anything we hand it is something it will attempt.
 */
const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'];

const JPEG_QUALITY = 0.9;
const INTERMEDIATE_JPEG_QUALITY = 1;

/**
 * Returns a file this browser can draw. A HEIC photo the browser cannot decode (Chrome, for one)
 * is converted to JPEG, so the draft preview, the uploading message, the dimensions sent with the
 * attachment and the uploaded file all agree. Anything else, including HEIC in a browser that
 * decodes it natively, comes back untouched.
 *
 * Support is detected by trying to decode the file, never by browser name. The decoder is a large
 * WASM chunk loaded only when a conversion is needed; it decodes in its own worker.
 */
export async function toBrowserDecodableImage(file: File): Promise<File> {
  if (!(await isHeif(file)) || (await canBrowserDecodeImage(file))) {
    return file;
  }

  try {
    const { heicTo } = await import('heic-to');
    const icc = readHeifIccProfile(await file.arrayBuffer());
    // With a profile to convert from, the first JPEG is an intermediate, so it is kept near
    // lossless and the one lossy encode happens in the sRGB pass.
    const jpeg = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: icc ? INTERMEDIATE_JPEG_QUALITY : JPEG_QUALITY,
    });
    const srgb = icc ? await toSrgbJpeg(await embedIccProfile(jpeg, icc), JPEG_QUALITY) : jpeg;
    return new File([srgb], withJpegExtension(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (error) {
    // Upload the original rather than lose the attachment; it renders once imgix serves it.
    console.error('HEIC conversion failed', error);
    return file;
  }
}

/** Reads the container brand from the file's bytes, since `type` is often empty for `.heic`. */
async function isHeif(file: Blob): Promise<boolean> {
  const header = await file.slice(0, 12).arrayBuffer();
  if (header.byteLength < 12) {
    return false;
  }
  const boxType = new TextDecoder().decode(header.slice(4, 8));
  const brand = new TextDecoder().decode(header.slice(8, 12));
  return boxType === 'ftyp' && HEIF_BRANDS.includes(brand);
}

function canBrowserDecodeImage(file: Blob): Promise<boolean> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    image.src = url;
  });
}

function withJpegExtension(name: string): string {
  return `${name.replace(/\.[^./]+$/, '')}.jpg`;
}
